// The ship's own predicted path, as a line.
//
// The prediction is the sim's OWN integrator run forward: `stepFlight` over
// the same pulling set the tick hands it, on a scratch copy of the flight
// state. There is no second model of the flight here, and that is the whole
// point of the line - whatever the assist does to the path, the line shows it,
// which only holds while the two are the same code.
//
// What the prediction assumes, because a line has to assume something:
//
//   the clock   `dt` and the assist take SIM seconds and the attitude takes
//               REAL ones, exactly as `stepFlight` does per frame, so one step
//               of `stepS` sim seconds is `stepS / rate` real ones. Warped up,
//               the line looks further ahead and the hand does proportionally
//               less inside it, which is what the sim does.
//   the hand    whatever is held now stays held for `HOLD_REAL_S` REAL
//               seconds, and the rest of the horizon coasts. Nobody holds a
//               burn for the year a heliocentric horizon covers, and a line
//               that assumed they did would be a picture of nothing. The
//               handle reports which of the two the line is, so the panel can
//               say so rather than leaving it silent.
//   the bodies  move, on the same clock everything else does. The prediction
//               runs in the frame the flight step runs in - relative to the
//               body pulling hardest - so that body's own term is exact at any
//               horizon and only the OTHERS have to be re-sampled against time.
//               Each of those is evaluated at a handful of instants across the
//               horizon once per rebuild and read back by a cubic through each
//               instant's position and velocity; a body whose own orbit that
//               grid cannot resolve rides its parent instead, keeping the
//               phase it had at the rebuild rather than being interpolated
//               through a grid that aliases it.
//
// A build is throttled, and the ship flies on between two of them. So the
// FIRST DRAWN vertex is not a knot at all: it carries the ship's own live
// offset from the anchor, rewritten every frame, and the draw range starts
// there. Without it the line's near end sits where the ship was at the last
// build - trailing the ship by a rebuild period of travel and snapping forward
// whenever one lands. `tools/preview/orbit-rings.mjs --trajectory` reads that
// vertex against the ship's own drawn position on the same frame.
//
// The knots are ECLIPTIC metre offsets from the build's own anchor, carried
// under the -90 degree rotation about x that is `eclipticToScene`. Offsets
// rather than absolute positions because a float32 vertex at a heliocentric
// 1.5e11 m quantises to 16 km steps, and the caller registers the line at the
// anchor in the camera-relative frame the orbit rings use.
//
// That anchor is the frame parent's LIVE centre plus the ship's parent-relative
// place at the build, which is how a satellite's orbit ring is registered
// (`src/atlas/orrery.ts`) and for the same reason: the parent moving under the
// drawing costs nothing, and by construction the far end can never sit where
// the parent used to be. The offsets stay small either way, so the float32
// vertex keeps the precision the paragraph above bought.
//
// The far end names itself. The integration stops at the FIRST of four things
// and marks which: the path meets the surface of a body it can reach, it comes
// back to where it started, the body pulling hardest stops being that body, or
// none of the three happened inside the step budget. The fourth is a state of
// its own and not a silence, because a line that merely ran out and a line
// that ended somewhere are the same picture otherwise - which is the whole
// fault this end exists to close.
//
// The name is drawn AT the far end rather than in the pilot panel: the end can
// be 190,000 km from the ship on the boot pose, so the eye that is asking the
// question is already out there, and a point and a caption at a constant pixel
// size read the same at any range.

import * as THREE from 'three';

import {
  copyAssistAim,
  stepFlight,
  type FlightAxes, type FlightConfig, type FlightField, type FlightPull,
  type FlightState, type FlightVec,
} from './flight.ts';
import { createGroundHit, surfaceSweep, type SurfaceReader } from './ground.ts';
import type { WorldVec } from '../world/frame.ts';
import { clamp } from '../math/angles.ts';

/** The ship's own path, in a colour no orbit ring uses. */
export const TRAJECTORY_COLOR = 0xaaff33;
export const TRAJECTORY_OPACITY = 0.9;

/** Drawn points. The orbit rings' own count, and the same reason: it is enough. */
const KNOTS = 256;
/** Integration steps between two drawn knots. */
const STEPS_PER_KNOT = 8;
const STEPS = (KNOTS - 1) * STEPS_PER_KNOT;

/**
 * How far ahead the line looks when nothing pins it, sim seconds.
 *
 * One orbit about whatever is pulling hardest when the ship is bound to it,
 * so a closed loop is the answer to "am I in orbit yet" and an open arc is the
 * answer to "no"; three range-crossings when it is not. That two-body reading
 * chooses the HORIZON and nothing else - the path itself is integrated against
 * every pull, the same set the tick uses.
 */
const MIN_HORIZON_S = 300;
const MAX_HORIZON_S = 2 * 365.25 * 86400;
const OPEN_RANGE_SPANS = 3;
/**
 * A bound horizon's margin over the raw two-body period, and its cap.
 *
 * A closed loop's own path returns a little after one period once the
 * dominant pull is not the only one acting, so a horizon of exactly one
 * period ends the prediction before the closure gate (`CLOSE_ARM_FRAC`) has
 * re-entered the ball. The cap keeps a year-long heliocentric period from
 * adding weeks to its own horizon.
 */
const HORIZON_MARGIN_FRAC = 0.1;
const HORIZON_MARGIN_CAP_S = 86400;

/**
 * How near its own start a path has to come back before it counts as closed,
 * as a share of the widest it ever stood from that start.
 *
 * A share rather than a distance because the loops this reads are 3,000 km
 * across over a low park and 40,000 km across over a high one. The ball is
 * entered a fraction of a knot before the true return, so the step inside it
 * is carried on to the closest approach and the end is marked THERE - the
 * entry is a trigger, the minimum is the answer.
 */
const CLOSE_ARM_FRAC = 0.01;
/**
 * How much of a revolution has to be behind the path before a closure is
 * looked for at all, as a share of the TWO-BODY period.
 *
 * The period and not the horizon: a pinned horizon is a question about how far
 * to look, not a claim about how long the orbit is, and gating on it would
 * make a pin of ten periods miss the first nine closures.
 */
const CLOSE_ARM_AFTER = 0.5;
/** Bisection steps used to place a crossing on the chord it was found across. */
const CROSS_BISECT = 32;

/**
 * How many knot spans of closing the per-step ground test is armed over.
 *
 * A body joins the test for one span when its surface stands within that
 * span's own relative travel of the ship, and the frame parent is always in.
 * The travel is bounded by the ship's speed plus the body's own, both in the
 * frame; the reading is taken at the span's own midpoint, so half a span would
 * bound it and the rest is what two straight lines do not carry where both
 * paths are arcs. Phobos never outweighs Mars even at its own
 * surface, so a line that only ever tested the body pulling hardest drew a
 * Mars orbit straight through the moon; the reach is what puts a moon back in
 * the test without paying a sphere sweep on all 21 slots at every step.
 */
const GROUND_REACH_SPANS = 2;

/**
 * How long the hand is assumed to stay where it is, REAL seconds.
 *
 * Past this the line coasts. It bounds the extrapolation rather than the
 * horizon: a held burn is a claim about the next minute, the path it puts the
 * ship on is a claim about the whole horizon, and they are different claims.
 */
const HOLD_REAL_S = 60;

/** Rebuild no faster than this, ms. 2040 steps over 21 pulls is not free. */
const REBUILD_MS = 200;

/**
 * Knots of a body's own orbit the sampling grid keeps, per revolution.
 *
 * The grid is read back by a cubic Hermite through each knot's position and
 * the ephemeris's own velocity there, the read `src/sim/flightCentres.ts`
 * gives the flight, so the error is a fraction of the body's own orbital
 * radius and never an absolute distance: 32 knots a revolution is 0.196 rad
 * between knots, and the cubic stands off the circle by at most 1/384 of that
 * to the fourth, 3.8e-6 of the radius. On Phobos's 9,376 km orbit that is
 * 37 m. It costs one Kepler solve per knot per body per rebuild and nothing
 * per step.
 */
const SAMPLES_PER_PERIOD = 32;
/** The most knots one body's grid holds. Past this it rides its parent. */
const SAMPLE_CAP = 512;
/** The fewest. A body that barely moves over the horizon still needs a cubic. */
const SAMPLE_MIN = 2;
/** Rebuild costs kept for `buildCosts()`, the newest last. */
const COST_WINDOW = 512;

/** Over the p95 and worst bars at once, so no cost-leg branch passes it. */
const SLOW_REBUILD_MS = 24;

/**
 * Mirrors `SLOW_REBUILD_MS` onto the frames that carry no rebuild at all,
 * which neither of that constant's bars reads: both come off the build
 * timer alone, and a frame with no rebuild never starts one. Bigger than
 * that constant on purpose: this box's own worst-frame noise on those
 * frames has read as high as the high 50s of ms with nothing injected, so
 * the busy wait has to clear that floor by a wide margin to read as an
 * injected fault rather than host jitter (`orbit-rings.mjs`'s
 * `COST_IDLE_LOUD_MS`).
 */
const SLOW_IDLE_MS = 80;

/** What the line currently is, for the panel to name. */
export type TrajectoryStatus = 'off' | 'coast' | 'held';

/**
 * What put the far end where it is.
 *
 * `budget` is the fourth state on purpose: it says none of the other three
 * fired inside the steps there were, so a line that merely stopped is not read
 * as a line that arrived somewhere.
 */
export type TrajectoryEndReason = 'ground' | 'closed' | 'left' | 'budget';

/** The far end's own colour, one per reason. Read by `orbit-rings.mjs --trajectory`. */
export const TRAJECTORY_END_COLOR: Readonly<Record<TrajectoryEndReason, number>> = {
  ground: 0xff5533,
  closed: 0x33ff99,
  left: 0x66ccff,
  budget: 0x9aa2aa,
};

/** Where the line ends, when, and why. */
export interface TrajectoryEnd {
  readonly reason: TrajectoryEndReason;
  /** Sim seconds past the build's own origin. */
  readonly atS: number;
  /** The drawn far end, as an ecliptic metre offset from the build's anchor. */
  readonly offset: FlightVec;
  /**
   * The same point in heliocentric ecliptic metres, in doubles, at the far
   * end's OWN time - the frame parent where it will be at `atS`, plus the
   * parent-relative place the integration reached. Not where the anchor was at
   * the rebuild, which is the whole point of the parent frame.
   */
  readonly at: WorldVec;
  /** The knot slot the far end is drawn in: the last vertex of the draw range. */
  readonly knot: number;
  /** The body the reading is about: the ground met, or the body left. */
  readonly bodyId: string | null;
  /** On `left`, the body that took over the pulling. Null otherwise. */
  readonly intoId: string | null;
}

/** The prediction the drawn line is: when it was built, from where, under what. */
export interface TrajectoryBuild {
  /** `flightSimSeconds` at the build. The line's own time origin. */
  readonly simSeconds: number;
  /** The first knot, heliocentric ecliptic metres in doubles, at the build. */
  readonly at: WorldVec;
  /**
   * The ship's place relative to `parentId` at the build, metres. The knots
   * are offsets from THIS, and the drawn anchor is that body's live centre
   * plus it, so a reader comparing a knot against a flown ship compares the
   * two relative to that body.
   */
  readonly rel: WorldVec;
  /**
   * The body the line is drawn about: the frame parent, or the moon whose
   * ground the line ends on inside it. Null for an inertial frame.
   */
  readonly parentId: string | null;
  /** Knots the sampling grid spent on the body it could resolve least. */
  readonly sampleKnots: number;
  /** Slots left riding a parent because the grid could not resolve them. */
  readonly rideCount: number;
  readonly knots: number;
  /** Sim seconds between two drawn knots. */
  readonly knotSpanS: number;
  /** Sim seconds one `stepFlight` call advances. One substep, by construction. */
  readonly stepS: number;
  readonly horizonS: number;
  /** The clock rate the build assumed, sim seconds per real second. */
  readonly rate: number;
  readonly throttle: number;
  readonly axes: FlightAxes;
  /** Whether the build let the assist act. */
  readonly assist: boolean;
  /** Steps the hand was assumed held for, of `STEPS`. */
  readonly holdSteps: number;
  /** True on the `?trajballistic=1` variant: no thrust and no assist. */
  readonly ballistic: boolean;
  /** Whether the two-body reading that chose the horizon found a closed orbit. */
  readonly bound: boolean;
  /** The two-body period the horizon derivation read, whatever the horizon is pinned to. */
  readonly periodS: number;
  /** What put the far end where it is. */
  readonly end: TrajectoryEnd;
}

export interface TrajectoryInput {
  readonly nowMs: number;
  readonly state: FlightState;
  /** The ship's own heliocentric ecliptic metres, AFTER this tick's advance. */
  readonly at: WorldVec;
  readonly pulls: readonly FlightPull[];
  /** One id per `pulls` slot. A slot nothing filled carries an empty string. */
  readonly pullIds: readonly string[];
  /** One `radius_m` per `pulls` slot, for the ground the path can meet. */
  readonly pullRadiiM: readonly number[];
  /**
   * The flight's own `FlightGround.surfaceAt` and `outerRadiiM`, so the line
   * ends on the drawn ground the flight stops on. Null leaves every slot on
   * `pullRadiiM`.
   */
  readonly surfaceAt: SurfaceReader | null;
  readonly pullOuterRadiiM: readonly number[] | null;
  /** One orbital period per `pulls` slot, sim seconds. 0 where there is none. */
  readonly pullPeriodS: readonly number[];
  /** The `pulls` slot of each slot's own parent, or -1 where it has none. */
  readonly pullParentIdx: readonly number[];
  /**
   * The slot of the body the ship's velocity is relative to, or -1 for an
   * inertial frame. The same pick `src/main.ts` makes for `FlightField.frameAccel`,
   * handed over rather than re-derived so the prediction and the tick can never
   * integrate in two different frames.
   */
  readonly framePullIdx: number;
  /**
   * Fill `out` with slot `i`'s heliocentric centre `aheadS` sim seconds past
   * now, and `vel` with its velocity there, m/s. Null holds every body where it
   * is, which is the pre-#379 reading.
   */
  readonly sampleAt: ((i: number, aheadS: number, out: WorldVec, vel: WorldVec) => void) | null;
  readonly axes: FlightAxes;
  readonly throttle: number;
  readonly rate: number;
  /** Whether the assist is on. */
  readonly assist: boolean;
  readonly simSeconds: number;
}

export interface TrajectoryConfig {
  /** The flight model's own config. Only the step sizes are replaced. */
  readonly config: FlightConfig;
  /**
   * `?trajballistic=1`: predict with no thrust and no assist, which is the
   * broken-on-purpose variant `orbit-rings.mjs --trajectory --inject
   * traj-ballistic` opens. Nothing a player reaches sets it.
   */
  readonly ballistic: boolean;
  /**
   * `?trajlag=1`: put the near end one frame behind the ship, which is the
   * broken-on-purpose variant `orbit-rings.mjs --trajectory --inject traj-lag`
   * opens - the smallest version of the fault that vertex exists to close.
   * Nothing a player reaches sets it.
   */
  readonly lagOneFrame: boolean;
  /**
   * `?trajrebuildstale=1`: put the near end one frame behind the ship on the
   * frames that carry a REBUILD and nowhere else - a build reading a pre-step
   * position while the trim between builds reads a post-step one.
   * `orbit-rings.mjs --trajectory --inject traj-rebuild-stale` opens it.
   * Nothing a player reaches sets it.
   */
  readonly rebuildStale: boolean;
  /**
   * `?trajnoresidue=1`: drop the float32 rounding residue from the drawn
   * anchor, registering the line at the bare anchor the way it did before
   * `rideResidue` existed. The control `orbit-rings.mjs --trajectory --inject
   * traj-no-residue` opens. Nothing a player reaches sets it.
   */
  readonly noResidue: boolean;
  /**
   * `?trajrunout=1`: never terminate, run the step budget out and mark the end
   * `budget` whatever the path did, which is what the line did before it named
   * its own far end. The control `orbit-rings.mjs --trajectory --inject
   * traj-runout` opens. Nothing a player reaches sets it.
   */
  readonly runOut: boolean;
  /**
   * The live heliocentric centre of a body, by id, for the drawn anchor. The
   * registry's own accessor; the line is registered against it every frame the
   * way a satellite's orbit ring is.
   */
  readonly bodyAt: (id: string) => WorldVec | null;
  /**
   * `?trajanchorpinned=1`: register the line at the frame parent's BUILD-time
   * place, so the parent walks out from under the drawing. The control
   * `orbit-rings.mjs --trajectory --inject traj-anchor-pinned` opens. Nothing a
   * player reaches sets it.
   */
  readonly pinnedAnchor: boolean;
  /**
   * `?trajbodiespinned=1`: sample the other bodies once at the rebuild and hold
   * them there for the whole horizon, which is what the line did before it
   * moved with them. The control `orbit-rings.mjs --trajectory --inject
   * traj-bodies-pinned` opens. Nothing a player reaches sets it.
   */
  readonly pinnedBodies: boolean;
  /**
   * `?trajslowrebuild=1`: add a fixed `SLOW_REBUILD_MS` inside every rebuild,
   * so the rebuild's own cost, the frame that carried it and nothing else
   * move. The control `orbit-rings.mjs --trajectory --inject
   * traj-slow-rebuild` opens it. Nothing a player reaches sets it.
   */
  readonly slowRebuild: boolean;
  /**
   * `?trajslowidle=1`: the mirror of `slowRebuild` - add a fixed
   * `SLOW_IDLE_MS` to every frame that does NOT carry a rebuild, so the box
   * reads loud while the rebuild itself stays cheap. The control
   * `orbit-rings.mjs --trajectory --inject traj-slow-idle` opens it. Nothing a
   * player reaches sets it.
   */
  readonly slowIdle: boolean;
  /**
   * `?trajhorizononeperiod=1`: zero the bound horizon's margin, restoring the
   * exact one-period horizon a closed loop used to end short against. The
   * control `orbit-rings.mjs --trajectory --inject horizon-one-period` opens
   * it. Nothing a player reaches sets it.
   */
  readonly horizonOnePeriod: boolean;
  /**
   * `?trajclosenosnap=1`: draw a closed loop's far end where the closure gate
   * sampled its closest approach instead of on the ship. The control
   * `orbit-rings.mjs --trajectory --inject close-no-snap` opens it. Nothing a
   * player reaches sets it.
   */
  readonly closeNoSnap: boolean;
  /**
   * `?trajclosenoresample=1`: snap a closed loop's far end onto the ship
   * without laying the flown arc between the last kept knot and it, so the
   * closing segment is drawn as a chord again. The control
   * `orbit-rings.mjs --trajectory --inject close-no-resample` opens it.
   * Nothing a player reaches sets it.
   */
  readonly closeNoResample: boolean;
  /**
   * `?trajcaptionperrebuild=1`: repaint the far end's caption on every rebuild
   * and drop it on every other one, so it blinks and re-inks at the rebuild
   * rate whatever the end is doing. The
   * control `orbit-rings.mjs --trajectory --inject caption-per-rebuild` opens
   * it. Nothing a player reaches sets it.
   */
  readonly captionPerRebuild: boolean;
  /**
   * Test the ground against the frame's dominant body alone, the way the line
   * did before it swept every body a knot span can reach - a path aimed at a
   * moon draws through it and ends on an orbit of the parent instead.
   * `tools/preview/ground-contact-check.mjs --inject trajectory-dominant-only`
   * is the control that sets it. Optional because no query parameter opens it
   * and the app never passes it: the harness builds this config itself.
   */
  readonly groundDominantOnly?: boolean;
  /**
   * Fire the ground test only on a step whose far end stands inside the body -
   * the gate the test ran behind before it swept every step, which misses a
   * step that enters and leaves a small body inside its own length.
   * `tools/preview/ground-contact-check.mjs --inject line-endpoint-gate` is the
   * control that sets it. Optional for the same reason as the field above.
   */
  readonly groundEndpointGate?: boolean;
  /**
   * Read the drawn ground under the chord's own nearest point to the centre
   * and sweep the whole step against that one radius, the way the ground test
   * read a shape body before it bisected the entry against the surface a
   * step's two ends actually cross. `ground-contact-check.mjs --inject
   * nearest-point` is the control that sets it. Optional for the same reason
   * as `groundDominantOnly`: no query parameter opens it and the app never
   * passes it.
   */
  readonly groundNearestPointOnly?: boolean;
  /**
   * `?trajdatumground=1`: end the line on every body's datum sphere, never
   * reading the drawn surface the flight stops on, which is where it ended
   * before it read one. `ground-contact-check.mjs --inject line-datum-sphere`
   * sets it under Node. Nothing a player reaches sets it.
   */
  readonly groundDatum: boolean;
  /**
   * `?trajcatmullrom=1`: read the sampling grid back by Catmull-Rom through
   * the positions alone, which is how the line placed every other body before
   * it read the ephemeris velocities. `ground-contact-check.mjs --inject
   * line-catmull-rom` sets it under Node. Nothing a player reaches sets it.
   */
  readonly catmullRom: boolean;
  /**
   * `?trajplanetframe=1`: keep drawing the line in the frame parent's frame
   * when it ends on a moon's ground, which leaves its end where the moon will
   * be while the moon is drawn where it is. `line-end-ground-check.mjs
   * --inject planet-frame` and `ground-contact-check.mjs --inject
   * line-planet-frame` open it.
   */
  readonly planetFrame: boolean;
  /**
   * `?trajchordend=1`: leave an end on a moon's ground where the step's own
   * straight line puts it, off the moon's orbit by the chord's bow.
   * `line-end-ground-check.mjs --warp 960 --inject chord-end` opens it.
   */
  readonly chordEnd: boolean;
  /**
   * Skip the anchor reset every build normally opens with, so a build that
   * lands the anchor on a moon's ground leaves it there even once a later
   * build no longer ends on that moon. `ground-contact-check.mjs --inject
   * line-anchor-sticky` is the control that sets it. Optional for the same
   * reason as `groundDominantOnly`: no query parameter opens it and the app
   * never passes it.
   */
  readonly groundAnchorSticky?: boolean;
}

export interface TrajectoryHandle {
  /** Register this in the camera-relative frame, at `anchor()`. */
  readonly line: THREE.Line;
  readonly enabled: boolean;
  /** The build's own anchor, heliocentric ecliptic metres. */
  anchor(): WorldVec;
  status(): TrajectoryStatus;
  setEnabled(on: boolean): void;
  /** Pin the horizon in sim seconds; 0 or less gives the derived one back. */
  setHorizon(s: number): void;
  /**
   * Whether the far end's caption sprite is allowed to draw. The point mark
   * and the line itself are not gated by this - only the text box, which is
   * the size of a HUD panel and reads as clutter once the camera has pulled
   * back to the Solar System view. `src/main.ts` drives this from which view
   * currently owns the Atlas's camera; the point of a settable field here
   * rather than a computed one is so a later toggle has something to hold.
   */
  readonly captionVisible: boolean;
  setCaptionVisible(on: boolean): void;
  built(): TrajectoryBuild | null;
  /** What the last rebuilds cost, ms, newest last. Read by the trajectory arm. */
  buildCosts(): number[];
  /** Rebuilds since the handle was made. A frame it grows on carried one. */
  builds(): number;
  resetCosts(): void;
  /**
   * Live toggle for `slowIdle`, starting from `config.slowIdle`: the cost leg
   * disarms it before its re-sample so the first sample reads loud and the
   * second reads clean.
   */
  setSlowIdle(on: boolean): void;
  update(input: TrajectoryInput): void;
  dispose(): void;
}

const ZERO_AXES: FlightAxes = { pitch: 0, yaw: 0, roll: 0 };

/**
 * How far ahead to look, and whether the ship is bound to what pulls hardest.
 *
 * Two-body about the dominant pull alone. It picks a horizon and says whether
 * the path closes; it does not move the ship.
 */
interface HorizonReading {
  horizonS: number;
  bound: boolean;
  /** The two-body period, sim seconds, or 0 on an open path. Unclamped. */
  periodS: number;
  /** The slot of the body pulling hardest at the anchor, or -1. */
  domIndex: number;
}

function deriveHorizon(
  at: WorldVec, pulls: readonly FlightPull[], velocity: FlightVec, horizonOnePeriod: boolean,
): HorizonReading {
  let bestGm = 0;
  let bestR = 0;
  let bestAccel = 0;
  let bestIdx = -1;
  for (let i = 0; i < pulls.length; i++) {
    const p = pulls[i]!;
    if (!(p.gm > 0)) continue;
    const r = Math.hypot(p.at.x - at.x, p.at.y - at.y, p.at.z - at.z);
    if (!(r > 0)) continue;
    const a = p.gm / (r * r);
    if (a <= bestAccel) continue;
    bestAccel = a;
    bestGm = p.gm;
    bestR = r;
    bestIdx = i;
  }
  if (!(bestGm > 0)) {
    return { horizonS: MIN_HORIZON_S, bound: false, periodS: 0, domIndex: -1 };
  }
  const v = Math.hypot(velocity.x, velocity.y, velocity.z);
  const energy = (v * v) / 2 - bestGm / bestR;
  if (energy < 0) {
    const a = -bestGm / (2 * energy);
    const period = 2 * Math.PI * Math.sqrt((a * a * a) / bestGm);
    const margin = horizonOnePeriod
      ? 0 : Math.min(period * HORIZON_MARGIN_FRAC, HORIZON_MARGIN_CAP_S);
    return {
      horizonS: clamp(period + margin, MIN_HORIZON_S, MAX_HORIZON_S),
      bound: true, periodS: period, domIndex: bestIdx,
    };
  }
  const open = v > 0 ? (OPEN_RANGE_SPANS * bestR) / v : MIN_HORIZON_S;
  return {
    horizonS: clamp(open, MIN_HORIZON_S, MAX_HORIZON_S),
    bound: false, periodS: 0, domIndex: bestIdx,
  };
}

/** Sim seconds as a span a caption has room for. */
function spanLabel(s: number): string {
  if (!(s > 0)) return '0 s';
  if (s < 90) return `${s.toFixed(0)} s`;
  if (s < 5400) return `${(s / 60).toFixed(1)} min`;
  if (s < 172800) return `${(s / 3600).toFixed(1)} h`;
  return `${(s / 86400).toFixed(1)} d`;
}

/** The two lines the far end's caption carries: what happened, and how far off. */
function endCaption(end: TrajectoryEnd): [string, string] {
  const body = (end.bodyId ?? '').toUpperCase();
  const span = spanLabel(end.atS);
  if (end.reason === 'ground') return ['GROUND', `${body} ${span}`.trim()];
  if (end.reason === 'closed') return ['CLOSED', `${body} ${span}`.trim()];
  if (end.reason === 'left') return [`LEAVES ${body}`.trim(), span];
  return ['FORECAST ENDS', span];
}

export function createTrajectory(config: TrajectoryConfig): TrajectoryHandle {
  const verts = new Float32Array(KNOTS * 3);
  const geometry = new THREE.BufferGeometry();
  const attr = new THREE.BufferAttribute(verts, 3);
  attr.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('position', attr);
  const material = new THREE.LineBasicMaterial({
    color: TRAJECTORY_COLOR,
    transparent: true,
    opacity: TRAJECTORY_OPACITY,
  });
  material.userData['baseOpacity'] = material.opacity;
  const line = new THREE.Line(geometry, material);
  line.name = 'ship-trajectory';
  // The knots are ecliptic offsets and everything under the Atlas is drawn in
  // scene axes; this is the one rotation between them.
  line.rotation.x = -Math.PI / 2;
  line.frustumCulled = false;
  line.visible = false;

  // ---------- The far end, and its name ----------
  // Both ride as children of the line, so they carry the same rotation and the
  // same camera-relative anchor and neither needs a registration of its own.
  // Both are sized in SCREEN space (`sizeAttenuation: false`): the end can be
  // 190,000 km away on one build and 60 km away on the next, and a marker that
  // shrank with range would say nothing at the range it matters most.
  const endVerts = new Float32Array(3);
  const endGeometry = new THREE.BufferGeometry();
  endGeometry.setAttribute('position', new THREE.BufferAttribute(endVerts, 3));
  const endMaterial = new THREE.PointsMaterial({
    color: TRAJECTORY_END_COLOR.budget,
    size: 11,
    sizeAttenuation: false,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
  });
  const endPoint = new THREE.Points(endGeometry, endMaterial);
  endPoint.name = 'ship-trajectory-end';
  endPoint.frustumCulled = false;
  endPoint.renderOrder = 3;
  line.add(endPoint);

  const capCanvas = document.createElement('canvas');
  capCanvas.width = 256;
  capCanvas.height = 80;
  const capCtx = capCanvas.getContext('2d');
  const capTexture = new THREE.CanvasTexture(capCanvas);
  capTexture.colorSpace = THREE.SRGBColorSpace;
  const capMaterial = new THREE.SpriteMaterial({
    map: capTexture, transparent: true, depthTest: false, sizeAttenuation: false,
  });
  const caption = new THREE.Sprite(capMaterial);
  caption.name = 'ship-trajectory-end-label';
  // Scale is in view-space units at unit depth with attenuation off, so this is
  // a fraction of the frame height: 0.085 is about 72 px at 850.
  caption.scale.set(0.085 * (capCanvas.width / capCanvas.height), 0.085, 1);
  // Anchored under its own bottom edge, so the caption hangs above the point
  // rather than over it, at any range.
  caption.center.set(0.5, -0.25);
  caption.frustumCulled = false;
  caption.renderOrder = 4;
  line.add(caption);

  let capKey = '';
  function paintCaption(end: TrajectoryEnd): void {
    const [top, foot] = endCaption(end);
    const key = `${top}|${foot}`;
    if ((key === capKey && !config.captionPerRebuild) || capCtx === null) return;
    capKey = key;
    const hex = `#${TRAJECTORY_END_COLOR[end.reason].toString(16).padStart(6, '0')}`;
    capCtx.clearRect(0, 0, capCanvas.width, capCanvas.height);
    capCtx.fillStyle = 'rgba(6, 10, 14, 0.68)';
    capCtx.fillRect(0, 0, capCanvas.width, capCanvas.height);
    capCtx.strokeStyle = hex;
    capCtx.lineWidth = 3;
    capCtx.strokeRect(1.5, 1.5, capCanvas.width - 3, capCanvas.height - 3);
    capCtx.textAlign = 'center';
    capCtx.textBaseline = 'middle';
    capCtx.fillStyle = hex;
    capCtx.font = 'bold 30px system-ui, sans-serif';
    capCtx.fillText(top, capCanvas.width / 2, 28, capCanvas.width - 16);
    capCtx.fillStyle = '#dfe6ec';
    capCtx.font = '24px system-ui, sans-serif';
    capCtx.fillText(foot, capCanvas.width / 2, 58, capCanvas.width - 16);
    capTexture.needsUpdate = true;
  }

  // The prediction's own state, so nothing here ever charges the player's own
  // `deltaVSpent` or moves the ship.
  const scratch: FlightState = {
    orientation: { x: 0, y: 0, z: 0, w: 1 },
    velocity: { x: 0, y: 0, z: 0 },
    deltaVSpent: 0,
    assistCatchUp: false,
    assistAim: null,
    // No ground is ever handed to the prediction, so it never lands; the
    // per-step ground test below is what ends a drawn path at a surface.
    grounded: null,
    groundOffset: { x: 0, y: 0, z: 0 },
    contactSpeedMps: 0,
    assistPush: { x: 0, y: 0, z: 0 },
    assistCapped: false,
  };
  const field: FlightField = { at: { x: 0, y: 0, z: 0 }, pulls: [] };
  const step: FlightVec = { x: 0, y: 0, z: 0 };
  // The build's own anchor, and what it is made of: the frame parent's centre
  // at the build plus the ship's place relative to it. `anchorNow()` composes
  // the second onto the parent's LIVE centre every time it is asked, so the
  // drawing rides the parent; `anchorAt` is the frozen fallback and what the
  // pinned control hands back.
  const anchorAt: WorldVec = { x: 0, y: 0, z: 0 };
  const anchorRel: WorldVec = { x: 0, y: 0, z: 0 };
  const anchorLive: WorldVec = { x: 0, y: 0, z: 0 };
  // What float32 threw away when `rideShip` stored the near end's offset, and
  // the registration point that carries it back.
  const rideResidue: WorldVec = { x: 0, y: 0, z: 0 };
  const anchorDrawn: WorldVec = { x: 0, y: 0, z: 0 };
  let anchorParentId: string | null = null;
  // One `stepFlight` call is one drift-kick-drift substep: `maxSubsteps` of 1
  // makes `flightSubsteps` return 1 whatever the step length works out to, so
  // the prediction's step is exactly the number below and not a function of
  // how far apart the knots happen to be.
  const predConfig: FlightConfig = {
    turnRate: config.config.turnRate,
    // A getter, not a copy: the rate outlives a single hull once a refit changes it.
    get accel(): number { return config.config.accel; },
    drag: config.config.drag,
    maxSubstepS: 0,
    maxSubsteps: 1,
    assistFree: config.config.assistFree,
    assistHeldOnly: config.config.assistHeldOnly,
    assistFrozenAim: config.config.assistFrozenAim,
    assistNoFloor: config.config.assistNoFloor,
  };

  // ---------- the other bodies, against time ----------
  //
  // One grid per pulling slot: the body's own heliocentric centre and velocity
  // at `gridN` + 1 instants across the horizon, read back by a cubic Hermite
  // between each pair (`catmullRom` lays a guard knot each side and reads the
  // positions alone). The grid is sized from the body's OWN period rather than
  // from the horizon, so a 5-minute forecast spends four knots on Mars and a
  // two-year one spends 266 on Mercury. A body needing more than `SAMPLE_CAP`
  // - every satellite, once the horizon is days rather than hours - rides its
  // parent at the offset it had at the rebuild instead: an aliased grid puts a
  // moon somewhere it has never been, and a frozen phase at least leaves it on
  // its own orbit.
  const predPulls: FlightPull[] = [];
  const gridBuf: Float64Array[] = [];
  const gridVel: Float64Array[] = [];
  const gridN: number[] = [];
  const gridH: number[] = [];
  const gridRide: number[] = [];
  let gridOff = new Float64Array(0);
  let sampleKnots = 0;
  let rideCount = 0;
  const _sample: WorldVec = { x: 0, y: 0, z: 0 };
  const _sampleV: WorldVec = { x: 0, y: 0, z: 0 };
  const _frameA: FlightVec = { x: 0, y: 0, z: 0 };

  // ---------- the ground, per body the path can reach ----------
  //
  // The slots the per-step ground test sweeps over the current knot span, each
  // one's radius, and where it stood at the previous step's start. The set is
  // rebuilt once a span at one distance per body carrying a radius; a body in
  // it costs one grid read a step, which is what keeps a 2,040-step rebuild
  // from sweeping the whole pulling set on every one of them.
  const groundSlots: number[] = [];
  const groundRadius: number[] = [];
  // The bound nothing of each body's drawn shape stands past; its datum on a sphere.
  const groundOuter: number[] = [];
  const groundHit = createGroundHit();
  let groundPrev = new Float64Array(0);
  // Each slot's own speed in the frame, read once a rebuild: the reach bound.
  let groundSpeed = new Float64Array(0);
  // Whether anything but the frame parent is in the set, so a span that sweeps
  // the parent alone never reads the parent's own grid.
  let groundOthers = 0;
  const _bodyAt: WorldVec = { x: 0, y: 0, z: 0 };
  const _domAt: WorldVec = { x: 0, y: 0, z: 0 };

  function ensurePredSlots(n: number): void {
    while (predPulls.length < n) {
      predPulls.push({ gm: 0, at: { x: 0, y: 0, z: 0 } });
      gridBuf.push(new Float64Array(0));
      gridVel.push(new Float64Array(0));
      gridN.push(0);
      gridH.push(0);
      gridRide.push(-1);
    }
    if (gridOff.length < n * 3) gridOff = new Float64Array(n * 3);
    if (groundSpeed.length < n) groundSpeed = new Float64Array(n);
    if (groundPrev.length < n * 3) groundPrev = new Float64Array(n * 3);
  }

  /** Lay every slot's grid for this horizon. `true` when there is one to read. */
  function buildGrids(input: TrajectoryInput, horizonS: number): boolean {
    const sample = input.sampleAt;
    if (sample === null) return false;
    const n = input.pulls.length;
    ensurePredSlots(n);
    sampleKnots = 0;
    rideCount = 0;
    for (let i = 0; i < n; i++) {
      const src = input.pulls[i]!;
      const slot = predPulls[i]!;
      slot.gm = src.gm;
      gridRide[i] = -1;
      gridN[i] = 0;
      if (!(src.gm > 0)) continue;
      const period = input.pullPeriodS[i] ?? 0;
      const want = period > 0
        ? Math.ceil((horizonS * SAMPLES_PER_PERIOD) / period) : SAMPLE_MIN;
      const parent = input.pullParentIdx[i] ?? -1;
      if (want > SAMPLE_CAP && parent >= 0 && parent < i) {
        const pc = input.pulls[parent]!.at;
        gridRide[i] = parent;
        gridOff[i * 3] = src.at.x - pc.x;
        gridOff[i * 3 + 1] = src.at.y - pc.y;
        gridOff[i * 3 + 2] = src.at.z - pc.z;
        rideCount++;
        continue;
      }
      const knots = clamp(want, SAMPLE_MIN, SAMPLE_CAP);
      const h = horizonS / knots;
      gridN[i] = knots;
      gridH[i] = h;
      if (knots > sampleKnots) sampleKnots = knots;
      if (gridBuf[i]!.length < (knots + 3) * 3) {
        gridBuf[i] = new Float64Array((knots + 3) * 3);
        gridVel[i] = new Float64Array((knots + 3) * 3);
      }
      const buf = gridBuf[i]!;
      const vel = gridVel[i]!;
      const guard = config.catmullRom ? 1 : 0;
      for (let k = -guard; k <= knots + guard; k++) {
        // Pinned, the grid is flat at the rebuild: the bodies are held where
        // they were and nothing else about the frame changes.
        sample(i, config.pinnedBodies ? 0 : k * h, _sample, _sampleV);
        const o = (k + 1) * 3;
        buf[o] = _sample.x;
        buf[o + 1] = _sample.y;
        buf[o + 2] = _sample.z;
        const still = config.pinnedBodies ? 0 : 1;
        vel[o] = _sampleV.x * still;
        vel[o + 1] = _sampleV.y * still;
        vel[o + 2] = _sampleV.z * still;
      }
    }
    return true;
  }

  /** Slot `i`'s own grid, read at `t` sim seconds past the build, into `out`. */
  function readGrid(i: number, t: number, out: WorldVec): void {
    const n = gridN[i]!;
    const h = gridH[i]!;
    let k = h > 0 ? Math.floor(t / h) : 0;
    if (k < 0) k = 0;
    if (k > n - 1) k = n - 1;
    const f = h > 0 ? t / h - k : 0;
    const f2 = f * f;
    const f3 = f2 * f;
    const buf = gridBuf[i]!;
    if (config.catmullRom) {
      readCatmullRom(buf, k * 3, f, f2, f3, out);
      return;
    }
    const vel = gridVel[i]!;
    const a = (k + 1) * 3;
    const b = a + 3;
    const h00 = 2 * f3 - 3 * f2 + 1;
    const h10 = (f3 - 2 * f2 + f) * h;
    const h01 = 3 * f2 - 2 * f3;
    const h11 = (f3 - f2) * h;
    out.x = h00 * buf[a]! + h10 * vel[a]! + h01 * buf[b]! + h11 * vel[b]!;
    out.y = h00 * buf[a + 1]! + h10 * vel[a + 1]! + h01 * buf[b + 1]! + h11 * vel[b + 1]!;
    out.z = h00 * buf[a + 2]! + h10 * vel[a + 2]! + h01 * buf[b + 2]! + h11 * vel[b + 2]!;
  }

  /** The control's read: four positions from knot `k - 1` at `o`, no velocity. */
  function readCatmullRom(
    buf: Float64Array, o: number, f: number, f2: number, f3: number, out: WorldVec,
  ): void {
    const ax = buf[o]!;
    const bx = buf[o + 3]!;
    const cx = buf[o + 6]!;
    const dx = buf[o + 9]!;
    out.x = 0.5 * (2 * bx + (cx - ax) * f
      + (2 * ax - 5 * bx + 4 * cx - dx) * f2 + (3 * bx - 3 * cx + dx - ax) * f3);
    const ay = buf[o + 1]!;
    const by = buf[o + 4]!;
    const cy = buf[o + 7]!;
    const dy = buf[o + 10]!;
    out.y = 0.5 * (2 * by + (cy - ay) * f
      + (2 * ay - 5 * by + 4 * cy - dy) * f2 + (3 * by - 3 * cy + dy - ay) * f3);
    const az = buf[o + 2]!;
    const bz = buf[o + 5]!;
    const cz = buf[o + 8]!;
    const dz = buf[o + 11]!;
    out.z = 0.5 * (2 * bz + (cz - az) * f
      + (2 * az - 5 * bz + 4 * cz - dz) * f2 + (3 * bz - 3 * cz + dz - az) * f3);
  }

  /** Every slot's centre at `t` sim seconds past the build, into `predPulls`. */
  function samplePulls(t: number): void {
    for (let i = 0; i < predPulls.length; i++) {
      const slot = predPulls[i]!;
      if (!(slot.gm > 0)) continue;
      const ride = gridRide[i]!;
      if (ride >= 0) {
        const pc = predPulls[ride]!.at;
        slot.at.x = pc.x + gridOff[i * 3]!;
        slot.at.y = pc.y + gridOff[i * 3 + 1]!;
        slot.at.z = pc.z + gridOff[i * 3 + 2]!;
        continue;
      }
      readGrid(i, t, slot.at);
    }
  }

  /**
   * One slot's centre at any `t`, rather than at the instant the whole set was
   * last held at. The ground test needs a body where it will BE at the step it
   * is tested across: Phobos covers 235 km of its own orbit in a knot span and
   * has an 11 km radius, so a moon held at its span's own sample is a moon the
   * test would meet at the wrong place or not at all. A slot whose grid rode
   * its parent is read through the parent, at the offset it was frozen with.
   */
  function centreAtT(i: number, t: number, out: WorldVec): void {
    const ride = gridRide[i]!;
    if (ride < 0) {
      readGrid(i, t, out);
      return;
    }
    centreAtT(ride, t, out);
    out.x += gridOff[i * 3]!;
    out.y += gridOff[i * 3 + 1]!;
    out.z += gridOff[i * 3 + 2]!;
  }

  /**
   * Every slot's own speed relative to the frame parent, metres a second, off
   * its grid over one knot span. Held for the whole rebuild: it sizes the
   * reach the ground test is armed over, and a near-circular orbit's speed is
   * the same at any instant of it.
   */
  function fillGroundSpeeds(input: TrajectoryInput, domIdx: number, spanS: number): void {
    const n = input.pulls.length;
    groundSpeed.fill(0);
    if (!(spanS > 0)) return;
    centreAtT(domIdx, 0, _domAt);
    const d0x = _domAt.x;
    const d0y = _domAt.y;
    const d0z = _domAt.z;
    centreAtT(domIdx, spanS, _domAt);
    const d1x = _domAt.x;
    const d1y = _domAt.y;
    const d1z = _domAt.z;
    for (let j = 0; j < n; j++) {
      if (j === domIdx) continue;
      if (!(input.pulls[j]!.gm > 0)) continue;
      if (!((input.pullRadiiM[j] ?? 0) > 0)) continue;
      centreAtT(j, 0, _bodyAt);
      const ax = _bodyAt.x - d0x;
      const ay = _bodyAt.y - d0y;
      const az = _bodyAt.z - d0z;
      centreAtT(j, spanS, _bodyAt);
      groundSpeed[j] = Math.hypot(
        _bodyAt.x - d1x - ax, _bodyAt.y - d1y - ay, _bodyAt.z - d1z - az) / spanS;
    }
  }

  /**
   * The bodies the next knot span's ground test sweeps, and where each stands
   * at the span's own start, as an offset from the anchor.
   *
   * The frame parent is always in: it is at rest in this frame by
   * construction, which is why its pair of centres is the one constant here.
   * Anything else joins when the span's relative travel can reach its surface.
   *
   * Asked of the centres the gravity sampling is already holding - the span's
   * own midpoint - against where the ship will be at that same instant, so a
   * span that sweeps nothing but the parent reads no grid at all. Reading one
   * per body per span instead cost half a millisecond of every rebuild on a
   * park where nothing is ever in reach.
   */
  function refreshGroundSet(
    input: TrajectoryInput, parentFrame: boolean, domIdx: number, domRadiusM: number,
    dcx: number, dcy: number, dcz: number, t0: number, spanS: number,
    px: number, py: number, pz: number,
  ): void {
    groundSlots.length = 0;
    groundRadius.length = 0;
    groundOuter.length = 0;
    groundOthers = 0;
    const outer = config.groundDatum ? null : input.pullOuterRadiiM;
    if (domRadiusM > 0) {
      groundSlots.push(domIdx);
      groundRadius.push(domRadiusM);
      groundOuter.push(Math.max(domRadiusM, outer?.[domIdx] ?? 0));
      groundPrev[0] = dcx;
      groundPrev[1] = dcy;
      groundPrev[2] = dcz;
    }
    if (config.groundDominantOnly === true) return;
    const speed = Math.hypot(
      scratch.velocity.x, scratch.velocity.y, scratch.velocity.z);
    const half = spanS / 2;
    const hx = px + scratch.velocity.x * half;
    const hy = py + scratch.velocity.y * half;
    const hz = pz + scratch.velocity.z * half;
    const set = parentFrame ? predPulls : input.pulls;
    const mcx = parentFrame ? predPulls[domIdx]!.at.x - dcx : input.at.x;
    const mcy = parentFrame ? predPulls[domIdx]!.at.y - dcy : input.at.y;
    const mcz = parentFrame ? predPulls[domIdx]!.at.z - dcz : input.at.z;
    let haveDom = false;
    for (let j = 0; j < set.length; j++) {
      if (j === domIdx) continue;
      const q = set[j]!;
      if (!(q.gm > 0)) continue;
      const r = input.pullRadiiM[j] ?? 0;
      if (!(r > 0)) continue;
      const bound = Math.max(r, outer?.[j] ?? 0);
      const mx = q.at.x - mcx;
      const my = q.at.y - mcy;
      const mz = q.at.z - mcz;
      const reach = GROUND_REACH_SPANS * spanS * (speed + (groundSpeed[j] ?? 0));
      if (Math.hypot(hx - mx, hy - my, hz - mz) > bound + reach) continue;
      // In the set: now the grid is worth reading, for where it stood at the
      // span's own start rather than at the midpoint the pass above compared.
      let cx = mx;
      let cy = my;
      let cz = mz;
      if (parentFrame) {
        if (!haveDom) {
          centreAtT(domIdx, t0, _domAt);
          haveDom = true;
        }
        centreAtT(j, t0, _bodyAt);
        cx = _bodyAt.x - _domAt.x + dcx;
        cy = _bodyAt.y - _domAt.y + dcy;
        cz = _bodyAt.z - _domAt.z + dcz;
      }
      const o = groundSlots.length * 3;
      groundSlots.push(j);
      groundRadius.push(r);
      groundOuter.push(bound);
      groundPrev[o] = cx;
      groundPrev[o + 1] = cy;
      groundPrev[o + 2] = cz;
      groundOthers++;
    }
  }

  /** The frame parent's own acceleration from everything else, at `predPulls`. */
  function frameAccelAt(domIdx: number): FlightVec {
    const c = predPulls[domIdx]!.at;
    _frameA.x = 0;
    _frameA.y = 0;
    _frameA.z = 0;
    for (let i = 0; i < predPulls.length; i++) {
      if (i === domIdx) continue;
      const p = predPulls[i]!;
      if (!(p.gm > 0)) continue;
      const dx = p.at.x - c.x;
      const dy = p.at.y - c.y;
      const dz = p.at.z - c.z;
      const r2 = dx * dx + dy * dy + dz * dz;
      if (!(r2 > 0)) continue;
      const k = p.gm / (r2 * Math.sqrt(r2));
      _frameA.x += dx * k;
      _frameA.y += dy * k;
      _frameA.z += dz * k;
    }
    return _frameA;
  }

  // `?trajlag=1` alone: the previous frame's place, to draw the near end at.
  const lagAt: WorldVec = { x: 0, y: 0, z: 0 };
  let haveLag = false;
  // Set by `update` for the one frame a build lands on, so the rebuild-stale
  // control can act there and only there.
  let builtThisFrame = false;

  let enabled = false;
  // What the last rebuilds cost, ms. A rebuild lands on ONE frame every 200 ms,
  // so the number that matters is this one against the frame budget, not an
  // average over the second it sits in.
  const costs: number[] = [];
  let buildCount = 0;
  // Starts armed from `config.slowIdle` (the `?trajslowidle=1` boot flag) but
  // is a live toggle from here: the cost leg disarms it with `setSlowIdle`
  // before its re-sample, the way `setParkRadialBasis` is flipped by a leg
  // rather than read once at boot.
  let slowIdleOn = config.slowIdle;
  let pinnedHorizonS = 0;
  let lastBuildMs = Number.NEGATIVE_INFINITY;
  let record: TrajectoryBuild | null = null;
  let disposed = false;

  function build(input: TrajectoryInput): void {
    // A busy wait, and inside `build()` rather than around it: `update()` times
    // this call for `buildCosts()` on the same frame.
    if (config.slowRebuild) {
      const until = performance.now() + SLOW_REBUILD_MS;
      while (performance.now() < until);
    }
    const { state, at, pulls, axes, throttle, rate, assist } = input;
    const derived = deriveHorizon(at, pulls, state.velocity, config.horizonOnePeriod);
    const horizonS = pinnedHorizonS > 0 ? pinnedHorizonS : derived.horizonS;
    const stepS = horizonS / STEPS;
    if (!(stepS > 0)) return;
    const knotSpanS = stepS * STEPS_PER_KNOT;
    const realStepS = rate > 0 ? stepS / rate : stepS;
    const holdSteps = realStepS > 0
      ? Math.min(STEPS, Math.floor(HOLD_REAL_S / realStepS)) : STEPS;
    predConfig.maxSubstepS = stepS;

    scratch.orientation.x = state.orientation.x;
    scratch.orientation.y = state.orientation.y;
    scratch.orientation.z = state.orientation.z;
    scratch.orientation.w = state.orientation.w;
    scratch.velocity.x = state.velocity.x;
    scratch.velocity.y = state.velocity.y;
    scratch.velocity.z = state.velocity.z;
    scratch.deltaVSpent = 0;
    // Carried, not reset: an armed catch-up is state the next tick will act on.
    scratch.assistCatchUp = state.assistCatchUp;
    copyAssistAim(state, scratch);
    field.pulls = pulls;

    // The body pulling hardest at the anchor: the frame the path is drawn in,
    // the ground it always tests, and the pull it can leave. It is the frame
    // the ship's own velocity is already in, so the tick's pick is taken rather
    // than re-derived; the ground test adds whatever else it can reach.
    const domIdx = input.framePullIdx >= 0 ? input.framePullIdx : derived.domIndex;
    const dom = domIdx >= 0 ? pulls[domIdx] ?? null : null;
    const domId = domIdx >= 0 ? input.pullIds[domIdx] ?? null : null;
    const domRadiusM = domIdx >= 0 ? input.pullRadiiM[domIdx] ?? 0 : 0;
    // The dominant body's centre as an offset from the anchor, so every test
    // below runs on the same small numbers the knots are in. In the parent
    // frame this is a CONSTANT - minus the ship's own parent-relative place -
    // rather than a centre captured at the rebuild, which is what makes the
    // ground test and the closure arm exact at any horizon.
    const dcx = dom !== null ? dom.at.x - at.x : 0;
    const dcy = dom !== null ? dom.at.y - at.y : 0;
    const dcz = dom !== null ? dom.at.z - at.z : 0;
    // The parent frame, if there is one to have: a frame parent and a sampler.
    ensurePredSlots(pulls.length);
    const parentFrame = input.framePullIdx >= 0 && dom !== null
      && buildGrids(input, horizonS);
    if (parentFrame) {
      field.pulls = predPulls;
      fillGroundSpeeds(input, domIdx, knotSpanS);
    } else {
      field.pulls = pulls;
      field.frameAccel = null;
      sampleKnots = 0;
      rideCount = 0;
      groundSpeed.fill(0);
    }
    const closeAfterS = CLOSE_ARM_AFTER * derived.periodS;
    const watch = !config.runOut && dom !== null;
    // The reader is asked only on a step inside a shape's outer bound, which
    // is a handful of steps on the approach and none anywhere else.
    const read = config.groundDatum ? null : input.surfaceAt;

    let endReason: TrajectoryEndReason = 'budget';
    let endT = horizonS;
    let endX = 0;
    let endY = 0;
    let endZ = 0;
    let intoIdx = -1;
    // The slot whose surface the path met, which is not always the frame's own.
    let groundIdx = -1;
    let stoppedAt = STEPS - 1;
    let maxArm = 0;
    // The closest the path has come back to its own start once the closure ball
    // has been entered, and where: the entry arms the reading, this answers it.
    let closeArm = Number.POSITIVE_INFINITY;
    let closeT = 0;
    let closeX = 0;
    let closeY = 0;
    let closeZ = 0;
    // The previous step's and the previous knot's own offsets, to place a
    // crossing on the segment it was found across.
    let sx = 0;
    let sy = 0;
    let sz = 0;
    let kx = 0;
    let ky = 0;
    let kz = 0;

    let px = 0;
    let py = 0;
    let pz = 0;
    verts[0] = 0;
    verts[1] = 0;
    verts[2] = 0;
    for (let i = 0; i < STEPS; i++) {
      const held = i < holdSteps;
      const spanEdge = i % STEPS_PER_KNOT === 0;
      if (parentFrame) {
        // The bodies at the middle of this KNOT's span, held across its steps,
        // and the ship where the parent will be plus the parent-relative place
        // reached. `-dc` is the ship's own parent-relative place at the build.
        //
        // Held rather than re-sampled per step because holding displaces the
        // ship and its parent TOGETHER: what survives into the parent frame is
        // only the change in the differential pull over half a knot span, which
        // at this horizon is nanometres, while re-sampling every step put a
        // 21-slot sweep and a frame acceleration on each of 2040 of them and
        // was five sixths of what a rebuild cost.
        if (spanEdge) {
          samplePulls((i + STEPS_PER_KNOT / 2) * stepS);
          field.frameAccel = frameAccelAt(domIdx);
        }
        const pc = predPulls[domIdx]!.at;
        field.at.x = pc.x - dcx + px;
        field.at.y = pc.y - dcy + py;
        field.at.z = pc.z - dcz + pz;
      } else {
        field.at.x = at.x + px;
        field.at.y = at.y + py;
        field.at.z = at.z + pz;
      }
      // After the sampling above, which is what the reach is asked against.
      if (watch && spanEdge) {
        refreshGroundSet(input, parentFrame, domIdx, domRadiusM,
          dcx, dcy, dcz, i * stepS, knotSpanS, px, py, pz);
      }
      stepFlight(
        scratch,
        held ? axes : ZERO_AXES,
        held && !config.ballistic ? throttle : 0,
        predConfig,
        stepS,
        held ? realStepS : 0,
        step,
        field,
        assist && held && !config.ballistic ? stepS : 0,
      );
      sx = px;
      sy = py;
      sz = pz;
      px += step.x;
      py += step.y;
      pz += step.z;
      stoppedAt = i;
      const knotEdge = (i + 1) % STEPS_PER_KNOT === 0;
      if (knotEdge) {
        const k = ((i + 1) / STEPS_PER_KNOT) * 3;
        verts[k] = px;
        verts[k + 1] = py;
        verts[k + 2] = pz;
      }
      if (!watch) {
        if (knotEdge) { kx = px; ky = py; kz = pz; }
        continue;
      }
      const tEnd = (i + 1) * stepS;

      // ----- the ground, per step, against every body in the span's reach -----
      // Each body is tested against the RELATIVE segment: where the ship
      // stands against where that body stood, at the step's two ends. For the
      // frame parent the two centres are the same constant and this is the
      // test it always was; for a moon the parent's own frame carries 2 km/s
      // that has to be divided out before a sphere 11 km across means anything.
      if (groundSlots.length > 0) {
        if (groundOthers > 0 && parentFrame) centreAtT(domIdx, tEnd, _domAt);
        let hitF = 2;
        let hitCx = 0;
        let hitCy = 0;
        let hitCz = 0;
        for (let g = 0; g < groundSlots.length; g++) {
          const j = groundSlots[g]!;
          const o = g * 3;
          let bx = dcx;
          let by = dcy;
          let bz = dcz;
          if (j !== domIdx) {
            if (parentFrame) {
              centreAtT(j, tEnd, _bodyAt);
              bx = _bodyAt.x - _domAt.x + dcx;
              by = _bodyAt.y - _domAt.y + dcy;
              bz = _bodyAt.z - _domAt.z + dcz;
            } else {
              bx = pulls[j]!.at.x - at.x;
              by = pulls[j]!.at.y - at.y;
              bz = pulls[j]!.at.z - at.z;
            }
          }
          const rx = px - bx;
          const ry = py - by;
          const rz = pz - bz;
          const radiusM = groundRadius[g]!;
          // The reach set says which bodies a step tests; it does not say
          // whether the test fires. A gate on the step's far end standing
          // inside the sphere misses a step that enters and leaves a small body
          // inside its own length, so every step in reach is swept.
          if (config.groundEndpointGate !== true
            || Math.hypot(rx, ry, rz) <= radiusM) {
            const f = surfaceSweep(read, j, radiusM, groundOuter[g]!,
              sx - groundPrev[o]!, sy - groundPrev[o + 1]!, sz - groundPrev[o + 2]!,
              rx, ry, rz, tEnd - stepS, tEnd, groundHit,
              config.groundNearestPointOnly === true);
            if (f >= 0 && f < hitF) {
              hitF = f;
              groundIdx = j;
              hitCx = groundPrev[o]! + (bx - groundPrev[o]!) * f;
              hitCy = groundPrev[o + 1]! + (by - groundPrev[o + 1]!) * f;
              hitCz = groundPrev[o + 2]! + (bz - groundPrev[o + 2]!) * f;
            }
          }
          groundPrev[o] = bx;
          groundPrev[o + 1] = by;
          groundPrev[o + 2] = bz;
        }
        if (groundIdx >= 0) {
          endReason = 'ground';
          endT = (i + hitF) * stepS;
          endX = sx + (px - sx) * hitF;
          endY = sy + (py - sy) * hitF;
          endZ = sz + (pz - sz) * hitF;
          if (parentFrame && groundIdx !== domIdx && !config.chordEnd) {
            // The sweep met the moon against its centre taken straight across the
            // step, and its orbit bows off that chord (13 m at Phobos): the end is
            // put against the moon where it stands at `endT`, where it is drawn.
            centreAtT(groundIdx, endT, _bodyAt);
            centreAtT(domIdx, endT, _domAt);
            endX += _bodyAt.x - _domAt.x + dcx - hitCx;
            endY += _bodyAt.y - _domAt.y + dcy - hitCy;
            endZ += _bodyAt.z - _domAt.z + dcz - hitCz;
          }
          break;
        }
      }

      // ----- the closure, per step -----
      const arm = Math.hypot(px, py, pz);
      if (arm > maxArm) maxArm = arm;
      if (derived.bound && tEnd >= closeAfterS && arm < CLOSE_ARM_FRAC * maxArm) {
        if (arm <= closeArm) {
          closeArm = arm;
          closeT = tEnd;
          closeX = px;
          closeY = py;
          closeZ = pz;
        } else {
          endReason = 'closed';
          endT = closeT;
          endX = closeX;
          endY = closeY;
          endZ = closeZ;
          break;
        }
      }

      // ----- the pull changing hands, per knot -----
      // Per knot and not per step: it is a comparison over the whole pulling
      // set where the other two are one distance each, and a knot is the
      // resolution the line is drawn at anyway. The crossing is then placed on
      // the knot's own chord, which is where the metres come back.
      if (knotEdge) {
        // The other bodies as offsets from the anchor at THIS instant: in the
        // parent frame that is their place relative to the parent, carried by
        // the same `-dc` the ship's own place is.
        const set = parentFrame ? predPulls : pulls;
        const pcx = parentFrame ? predPulls[domIdx]!.at.x - dcx : at.x;
        const pcy = parentFrame ? predPulls[domIdx]!.at.y - dcy : at.y;
        const pcz = parentFrame ? predPulls[domIdx]!.at.z - dcz : at.z;
        let bestA = 0;
        let bestIdx = -1;
        for (let j = 0; j < set.length; j++) {
          const q = set[j]!;
          if (!(q.gm > 0)) continue;
          const qx = px - (q.at.x - pcx);
          const qy = py - (q.at.y - pcy);
          const qz = pz - (q.at.z - pcz);
          const r2 = qx * qx + qy * qy + qz * qz;
          if (!(r2 > 0)) continue;
          const a = q.gm / r2;
          if (a <= bestA) continue;
          bestA = a;
          bestIdx = j;
        }
        if (bestIdx >= 0 && bestIdx !== domIdx) {
          const other = set[bestIdx]!;
          const ocx = other.at.x - pcx;
          const ocy = other.at.y - pcy;
          const ocz = other.at.z - pcz;
          // The dominant body still leads at the previous knot and does not
          // here, so the crossing is on this chord: bisect the one scalar that
          // changes sign across it.
          const lead = (f: number): number => {
            const qx = kx + (px - kx) * f;
            const qy = ky + (py - ky) * f;
            const qz = kz + (pz - kz) * f;
            const d2 = (qx - dcx) ** 2 + (qy - dcy) ** 2 + (qz - dcz) ** 2;
            const o2 = (qx - ocx) ** 2 + (qy - ocy) ** 2 + (qz - ocz) ** 2;
            return (dom!.gm / d2) - (other.gm / o2);
          };
          let lo = 0;
          let hi = 1;
          if (lead(0) > 0) {
            for (let b = 0; b < CROSS_BISECT; b++) {
              const mid = (lo + hi) / 2;
              if (lead(mid) > 0) lo = mid; else hi = mid;
            }
          } else {
            hi = 0;
          }
          endReason = 'left';
          intoIdx = bestIdx;
          endT = tEnd - (1 - hi) * knotSpanS;
          endX = kx + (px - kx) * hi;
          endY = ky + (py - ky) * hi;
          endZ = kz + (pz - kz) * hi;
          break;
        }
        kx = px;
        ky = py;
        kz = pz;
      }
    }
    if (endReason === 'budget') {
      endT = horizonS;
      endX = px;
      endY = py;
      endZ = pz;
    }
    // Which slot the far end is drawn in. The last knot the loop wrote may
    // already stand PAST the event - a crossing found on a knot edge lies
    // behind that knot - so it is overwritten when it does, and the event goes
    // in the next slot when it does not.
    const written = Math.floor((stoppedAt + 1) / STEPS_PER_KNOT);
    const endKnot = clamp(
      endT > written * knotSpanS ? written + 1 : written, 1, KNOTS - 1);
    verts[endKnot * 3] = endX;
    verts[endKnot * 3 + 1] = endY;
    verts[endKnot * 3 + 2] = endZ;
    if (!config.groundAnchorSticky) {
      anchorAt.x = at.x;
      anchorAt.y = at.y;
      anchorAt.z = at.z;
      anchorParentId = parentFrame ? domId : null;
      // The ship's own parent-relative place at the build: `-dc`, which is
      // what the live anchor adds to the parent's centre every frame.
      anchorRel.x = parentFrame ? -dcx : 0;
      anchorRel.y = parentFrame ? -dcy : 0;
      anchorRel.z = parentFrame ? -dcz : 0;
    }
    // Where the far end really is, in heliocentric metres, at its OWN time: the
    // parent sampled exactly at that instant rather than interpolated, plus the
    // parent-relative place the integration reached. A reader asking whether the
    // forecast lands on a body has to have this one and not the build's anchor.
    let endAtX = at.x + endX;
    let endAtY = at.y + endY;
    let endAtZ = at.z + endZ;
    if (parentFrame && input.sampleAt !== null) {
      input.sampleAt(domIdx, endT, _sample, _sampleV);
      endAtX = _sample.x - dcx + endX;
      endAtY = _sample.y - dcy + endY;
      endAtZ = _sample.z - dcz + endZ;
    }
    if (parentFrame && endReason === 'ground' && groundIdx >= 0 && groundIdx !== domIdx
      && !config.planetFrame) {
      toGroundFrame(input, domIdx, groundIdx, endKnot, knotSpanS, endT);
      endX = verts[endKnot * 3]!;
      endY = verts[endKnot * 3 + 1]!;
      endZ = verts[endKnot * 3 + 2]!;
    }
    attr.needsUpdate = true;
    geometry.computeBoundingSphere();
    record = {
      simSeconds: input.simSeconds,
      at: { x: at.x, y: at.y, z: at.z },
      rel: { x: anchorRel.x, y: anchorRel.y, z: anchorRel.z },
      parentId: anchorParentId,
      sampleKnots,
      rideCount,
      knots: KNOTS,
      knotSpanS,
      stepS,
      horizonS,
      rate,
      throttle,
      axes: { pitch: axes.pitch, yaw: axes.yaw, roll: axes.roll },
      assist,
      holdSteps,
      ballistic: config.ballistic,
      bound: derived.bound,
      periodS: derived.periodS,
      end: {
        reason: endReason,
        atS: endT,
        offset: { x: endX, y: endY, z: endZ },
        at: { x: endAtX, y: endAtY, z: endAtZ },
        knot: endKnot,
        bodyId: endReason === 'budget' ? null
          : endReason === 'ground' && groundIdx >= 0
            ? input.pullIds[groundIdx] ?? null : domId,
        intoId: intoIdx >= 0 ? input.pullIds[intoIdx] ?? null : null,
      },
    };
    endVerts[0] = 0;
    endVerts[1] = 0;
    endVerts[2] = 0;
    endPoint.position.set(endX, endY, endZ);
    caption.position.set(endX, endY, endZ);
    endMaterial.color.setHex(TRAJECTORY_END_COLOR[endReason]);
    paintCaption(record.end);
    line.visible = true;
  }

  /**
   * Put the drawn near end on the ship, this frame.
   *
   * The knots are a curve fixed in space and the ship rides along it, so the
   * part of it behind the ship is the past. This drops that part - the draw
   * range opens at the last knot the ship has already passed - and writes the
   * ship's own live offset into that one slot, so the line leaves the ship
   * rather than trailing it. The slot it overwrites is behind the ship and can
   * never be drawn again under this build, and the next build rewrites the
   * whole buffer anyway.
   */
  /**
   * Redraw a line that ends on a moon's ground about that moon: each knot loses
   * the moon's travel about the planet since the build, off the grid the ground
   * test read, and the line is registered at the moon's live centre.
   */
  function toGroundFrame(
    input: TrajectoryInput, domIdx: number, groundIdx: number, endKnot: number,
    knotSpanS: number, endT: number,
  ): void {
    centreAtT(groundIdx, 0, _bodyAt);
    centreAtT(domIdx, 0, _domAt);
    const m0x = _bodyAt.x - _domAt.x;
    const m0y = _bodyAt.y - _domAt.y;
    const m0z = _bodyAt.z - _domAt.z;
    for (let k = 1; k <= endKnot; k++) {
      const t = k === endKnot ? endT : k * knotSpanS;
      centreAtT(groundIdx, t, _bodyAt);
      centreAtT(domIdx, t, _domAt);
      const o = k * 3;
      verts[o] = verts[o]! - (_bodyAt.x - _domAt.x - m0x);
      verts[o + 1] = verts[o + 1]! - (_bodyAt.y - _domAt.y - m0y);
      verts[o + 2] = verts[o + 2]! - (_bodyAt.z - _domAt.z - m0z);
    }
    const moon = input.pulls[groundIdx]!.at;
    anchorParentId = input.pullIds[groundIdx] ?? null;
    anchorRel.x = input.at.x - moon.x;
    anchorRel.y = input.at.y - moon.y;
    anchorRel.z = input.at.z - moon.z;
  }

  /**
   * Where the line is registered this frame: the frame parent's LIVE centre
   * plus the ship's parent-relative place at the build.
   *
   * A satellite's orbit ring is registered at its parent the same way
   * (`src/atlas/orrery.ts`), and the reason is the same: the knots are a
   * parent-relative arc, so the parent moving under them costs nothing and the
   * far end can never be left standing where the parent used to be. Read per
   * frame rather than captured, including in the branch where the sim is paused
   * with the Solar System view open and nothing rebuilds.
   */
  function anchorNow(): WorldVec {
    if (config.pinnedAnchor || anchorParentId === null) return anchorAt;
    const p = config.bodyAt(anchorParentId);
    if (p === null) return anchorAt;
    anchorLive.x = p.x + anchorRel.x;
    anchorLive.y = p.y + anchorRel.y;
    anchorLive.z = p.z + anchorRel.z;
    return anchorLive;
  }

  function rideShip(input: TrajectoryInput): void {
    const b = record;
    if (b === null) return;
    const keep = config.lagOneFrame || config.rebuildStale;
    const lag = haveLag
      && (config.lagOneFrame || (config.rebuildStale && builtThisFrame));
    // Read before the store: under the control these are the same object.
    const ax = lag ? lagAt.x : input.at.x;
    const ay = lag ? lagAt.y : input.at.y;
    const az = lag ? lagAt.z : input.at.z;
    if (keep) {
      lagAt.x = input.at.x;
      lagAt.y = input.at.y;
      lagAt.z = input.at.z;
      haveLag = true;
    }
    const elapsed = input.simSeconds - b.simSeconds;
    const passed = b.knotSpanS > 0 ? Math.ceil(elapsed / b.knotSpanS) : 1;
    // Clamped to the far end's own slot rather than to the buffer: past the
    // end the knots are stale and the next build lands within a rebuild period.
    const k = clamp(passed, 1, b.end.knot) - 1;
    // Against the LIVE anchor, which is what the line is drawn at: the ship and
    // the anchor both ride the parent, so this offset is the same one #362's
    // marker gets from `simApi.ship.at()` and the two cannot open a gap.
    const a = anchorNow();
    const nx = ax - a.x;
    const ny = ay - a.y;
    const nz = az - a.z;
    verts[k * 3] = nx;
    verts[k * 3 + 1] = ny;
    verts[k * 3 + 2] = nz;
    // The offset is stored in float32 and reaches the orbit's own arm between
    // builds under warp, where a rounded metre is a decimetre and the bridge
    // camera stands 17 m off the hull; registering the line at the rounding's
    // own residue puts the rounded knot back on the ship to the metre.
    rideResidue.x = nx - verts[k * 3]!;
    rideResidue.y = ny - verts[k * 3 + 1]!;
    rideResidue.z = nz - verts[k * 3 + 2]!;
    // A closed loop's far end IS its near end. The closure gate marks the end
    // at the step it sampled nearest the start, which is up to `CLOSE_ARM_FRAC`
    // of the arm short of it - kilometres over a park, invisible at system
    // scale and a hundred pixels at the chase camera - and the ship then rides
    // on while that point stands still, so the gap grows between rebuilds and
    // the mark and caption riding it swing across the frame. Only the DRAWING
    // moves: `end.offset` still carries where the integration came back, which
    // is what the closure reading is. Read back off `verts[k*3]` rather than
    // off `nx/ny/nz` directly: those already carry whatever the anchor's own
    // residue correction just left in the buffer, and the far end has to match
    // the near end's drawn place, not its pre-rounding one.
    let last = b.end.knot;
    if (b.end.reason === 'closed' && !config.closeNoSnap) {
      const dx = verts[k * 3]!;
      const dy = verts[k * 3 + 1]!;
      const dz = verts[k * 3 + 2]!;
      // The knots the ship has already passed are the arc between the far end
      // and the ship: slot j held the ship's own live offset while it stood
      // between knot j and knot j+1, and the loop closes, so laying slots
      // 1..k-1 into the free tail past the far end puts a knot every span or
      // two along the closing arc. Without them the far end is snapped onto a
      // ship that has ridden k knots on from where the closure sampled, and
      // the one segment joining the last kept knot to it is drawn as a chord
      // across that much of the loop - a fifth of it at a day a second.
      const room = KNOTS - 1 - b.end.knot;
      if (room > 0 && k > 0 && !config.closeNoResample) {
        // Every stride'th knot where the arc is longer than the tail is free,
        // so the walk can never run past the buffer's last slot.
        const stride = Math.max(1, Math.ceil(k / room));
        for (let j = stride; j < k; j += stride) {
          last++;
          verts[last * 3] = verts[j * 3]!;
          verts[last * 3 + 1] = verts[j * 3 + 1]!;
          verts[last * 3 + 2] = verts[j * 3 + 2]!;
        }
        last++;
      }
      verts[last * 3] = dx;
      verts[last * 3 + 1] = dy;
      verts[last * 3 + 2] = dz;
      endPoint.position.set(dx, dy, dz);
      caption.position.set(dx, dy, dz);
    }
    attr.needsUpdate = true;
    geometry.setDrawRange(k, last - k + 1);
  }

  return {
    line,
    get enabled(): boolean { return enabled; },
    get captionVisible(): boolean { return caption.visible; },
    setCaptionVisible(on: boolean): void {
      caption.visible = config.captionPerRebuild ? on && buildCount % 2 === 0 : on;
    },
    anchor(): WorldVec {
      const a = anchorNow();
      anchorDrawn.x = a.x + (config.noResidue ? 0 : rideResidue.x);
      anchorDrawn.y = a.y + (config.noResidue ? 0 : rideResidue.y);
      anchorDrawn.z = a.z + (config.noResidue ? 0 : rideResidue.z);
      return anchorDrawn;
    },
    status(): TrajectoryStatus {
      if (!enabled) return 'off';
      const b = record;
      if (b === null || b.holdSteps <= 0) return 'coast';
      const hand = b.throttle !== 0 || b.axes.pitch !== 0 || b.axes.yaw !== 0;
      return hand ? 'held' : 'coast';
    },
    setEnabled(on: boolean): void {
      if (enabled === on) return;
      enabled = on;
      if (!on) {
        line.visible = false;
        record = null;
        anchorParentId = null;
        lastBuildMs = Number.NEGATIVE_INFINITY;
        haveLag = false;
        rideResidue.x = 0;
        rideResidue.y = 0;
        rideResidue.z = 0;
      }
    },
    setHorizon(s: number): void {
      pinnedHorizonS = Number.isFinite(s) && s > 0 ? s : 0;
      lastBuildMs = Number.NEGATIVE_INFINITY;
    },
    built(): TrajectoryBuild | null { return record; },
    buildCosts(): number[] { return costs.slice(); },
    builds(): number { return buildCount; },
    resetCosts(): void { costs.length = 0; },
    setSlowIdle(on: boolean): void { slowIdleOn = on === true; },
    update(input: TrajectoryInput): void {
      if (disposed || !enabled) return;
      builtThisFrame = input.nowMs - lastBuildMs >= REBUILD_MS;
      if (builtThisFrame) {
        lastBuildMs = input.nowMs;
        const t0 = performance.now();
        build(input);
        buildCount++;
        costs.push(performance.now() - t0);
        if (costs.length > COST_WINDOW) costs.shift();
      } else if (slowIdleOn) {
        // The mirror of the busy wait inside `build()`: this frame carries no
        // rebuild, so nothing here is timed into `costs`.
        const until = performance.now() + SLOW_IDLE_MS;
        while (performance.now() < until);
      }
      rideShip(input);
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      line.visible = false;
      record = null;
      line.remove(endPoint);
      line.remove(caption);
      geometry.dispose();
      material.dispose();
      endGeometry.dispose();
      endMaterial.dispose();
      capTexture.dispose();
      capMaterial.dispose();
    },
  };
}
